import axios, {
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { environment } from '@/config/environment';
import {
  getAuthorizationToken,
  getBackendSessionHeaderValue,
  hasBackendSessionPermission,
  OTD_SESSION_ID_HEADER,
  refreshTokenAndUpdateAuthData,
} from '@/providers/authProvider';
import { serializeApiParams } from '@/api/queryParams';

const responseCache = new Map<string, AxiosResponse>();

export const API_CACHE_BYPASS_PATTERNS = [
  '/order/get',
  '/order/update',
  '/Order',
  '/Order/update',
  '/orderproduct',
  '/orderproduct/get',
  '/orderproduct/update',
  '/orderproduct/delete',
  '/delivery/get',
  '/delivery/update',
  '/simpleorder/get',
  '/simpleorder',
  '/upload/file',
  '/download',
  '/bulkordertemplate/get',
  '/customer/get',
  '/CustomerExpanded',
  '/.auth',
  '/expandedcustomer/get',
  '/expandedmaterial/get',
  '/expandedmaterial',
  '/expandedsupplypoint/get',
  '/expandedsupplypoint',
  '/inventoryGrading',
  '/scheduling/get',
  '/scheduling',
  '/Scheduling',
  '/inventoryRailcars',
  '/inventoryProductAvailability',
  '/shipment_supplypoint',
  '/plantroutes/get',
  '/inventoryReplenisher/sto',
  '/inventoryReplenisher/adjustment',
  'notifypartners/get',
  '/notifypartners',
  'notifypartner/get',
  '/route/get',
  '/Route',
  'bulkorders/upload',
  '/bulkorders/upload',
  'consignee/get',
  '/consignee',
  'inventoryReplenisher/recalculate',
  'inventoryReplenisher/checkstatus',
  'inventoryReplenisher/lastupdated',
  'freightpayers/get',
  '/freightpayers',
  '/unitofmeasure',
  '/cyberorderstatus',
  '/orderTypes',
  '/drccodes',
  '/freightforwarders',
  '/incoterms',
  '/incotermpairs',
  '/materialUnitsOfMeasure',
  '/ShippingCondition',
  '/containertypes',
  '/Currency',
  '/plannedtransportationdates',
  '/planningdates/requesteddate',
  '/note',
  '/me',
] as const;

const AUTH_EXCEPTION_PATTERNS = ['session', 'dates', 'timezone', 'auth', 'me', 'inventoryGrading', 'unitofmeasure'];

const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url);

const isLocalMockAuthEnabled = () => ['localhost', '127.0.0.1'].includes(window.location.hostname) && environment.localAuth.enabled;

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const trimLeadingSlash = (value: string) => value.replace(/^\/+/, '');

export const buildApiUrl = (url: string | undefined, apiEndpoint: string) => {
  if (!url) {
    return undefined;
  }

  if (isAbsoluteUrl(url) || url.includes('.auth')) {
    return url;
  }

  const normalizedApiEndpoint = trimTrailingSlash(apiEndpoint);
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;

  if (normalizedApiEndpoint.startsWith('/') && normalizedUrl === normalizedApiEndpoint) {
    return normalizedUrl;
  }

  if (normalizedApiEndpoint.startsWith('/') && normalizedUrl.startsWith(`${normalizedApiEndpoint}/`)) {
    return normalizedUrl;
  }

  return `${normalizedApiEndpoint}/${trimLeadingSlash(normalizedUrl)}`;
};

export const shouldBypassCache = (url?: string) => {
  if (!url) {
    return true;
  }

  return API_CACHE_BYPASS_PATTERNS.some((pattern) => url.includes(pattern));
};

export const buildResponseCacheKey = (config: Pick<AxiosRequestConfig, 'url' | 'data'>) =>
  `${config.url ?? ''}:${JSON.stringify(config.data ?? null)}`;

const authDebugLog = (event: string, details?: Record<string, unknown>) => {
  if (!environment.authDebug) {
    return;
  }

  const payload = {
    at: new Date().toISOString(),
    event,
    ...(details ?? {}),
  };

  console.info('[auth-flow]', payload);
};

let permissionDeniedHandler: (() => void) | null = null;
let authRefreshFailedHandler: (() => void) | null = null;
let refreshTokenPromise: Promise<string | null> | null = null;

export const setPermissionDeniedHandler = (handler: () => void) => {
  permissionDeniedHandler = handler;
};

export const setAuthRefreshFailedHandler = (handler: () => void) => {
  authRefreshFailedHandler = handler;
};

export const setSessionExpiredHandler = setPermissionDeniedHandler;

export const axiosInstance: AxiosInstance = axios.create({
  timeout: environment.globalTimeout,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  paramsSerializer: {
    serialize: (params) => serializeApiParams(params as Record<string, unknown>),
  },
});

const shouldSkipPermissionCheck = (url?: string) => {
  if (!url) {
    return true;
  }

  return AUTH_EXCEPTION_PATTERNS.some((pattern) => url.includes(pattern));
};

const buildAuthHeaders = (configHeaders: AxiosRequestConfig['headers'], token: string) => {
  const headers = AxiosHeaders.from(configHeaders as never);
  const backendSessionId = getBackendSessionHeaderValue();

  if (token && !isLocalMockAuthEnabled()) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (backendSessionId) {
    headers.set(OTD_SESSION_ID_HEADER, backendSessionId);
  }

  return headers;
};

const hasApiAccess = async (token: string) => {
  return hasBackendSessionPermission(token);
};

  


export const appendPermissionToBody = (body: unknown, permission: boolean, url?: string): unknown => {
  if (/\/(?:inventoryGrading)(?:\/(?:block|unrestrict|update))?\/?$/i.test(url ?? '')) {
    return body;
  }

  const inventoryEndpoint = /\/(inventoryProductAvailability|inventoryRailcars)\/?(?:\?.*)?$/i.exec(url ?? '')?.[1]?.toLowerCase();
  const applyPermission = (value: Record<string, unknown>) => {
    if (inventoryEndpoint) {
      const { Permission: _legacyPermission, ...payload } = value;
      return { ...payload, permission: inventoryEndpoint === 'inventoryproductavailability' ? String(permission) : permission };
    }
    return { ...value, Permission: permission };
  };
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return body;
      }
      return JSON.stringify(applyPermission(parsed));
    } catch {
      return body;
    }
  }

  if (Array.isArray(body) || body instanceof FormData || body instanceof URLSearchParams) {
    return body;
  }

  if (body && typeof body === 'object') {
    return applyPermission(body as Record<string, unknown>);
  }

  return body;
};

const isPermissionDeniedError = (error: unknown) => {
  const response = (error as {
    response?: {
      data?: unknown;
      statusText?: string;
    };
  }).response;

  const responseText = typeof response?.data === 'string' ? response.data : '';
  const statusText = typeof response?.statusText === 'string' ? response.statusText : '';

  return responseText.includes('Permission denied') || statusText.includes('Permission denied');
};

axiosInstance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  config.url = buildApiUrl(config.url, environment.apiEndpoint);

  const token = await getAuthorizationToken();
  config.headers = buildAuthHeaders(config.headers, token);

  authDebugLog('axios.request', {
    url: config.url,
    hasToken: !!token,
  });

  if (!isLocalMockAuthEnabled() && !shouldSkipPermissionCheck(config.url)) {
    authDebugLog('axios.permissionCheck.start', { url: config.url });
    const hasPermission = await hasApiAccess(token);

    if (hasPermission !== null) {
      config.data = appendPermissionToBody(config.data, hasPermission, config.url);
      authDebugLog('axios.permissionCheck.done', {
        url: config.url,
        permission: hasPermission,
      });
    } else {
      authDebugLog('axios.permissionCheck.skipped', {
        url: config.url,
        reason: 'backend_session_not_ready',
      });
    }
  }

  if (!shouldBypassCache(config.url)) {
    const key = buildResponseCacheKey(config);
    const cachedResponse = responseCache.get(key);

    if (cachedResponse) {
      config.adapter = () => Promise.resolve(cachedResponse);
    }
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    if (!shouldBypassCache(response.config.url)) {
      responseCache.set(buildResponseCacheKey(response.config), response);
    }

    return response;
  },
  async (error) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    authDebugLog('axios.401', {
      url: error?.config?.url,
      statusText: error?.response?.statusText ?? '',
    });

    if (isPermissionDeniedError(error)) {
      authDebugLog('axios.401.permissionDenied', { url: error?.config?.url });
      permissionDeniedHandler?.();
      return Promise.reject(error);
    }

    if (!environment.authRefreshEnabled) {
      authDebugLog('axios.refresh.skipped', { reason: 'refresh_disabled' });
      authRefreshFailedHandler?.();
      return Promise.reject(error);
    }

    if (!refreshTokenPromise) {
      authDebugLog('axios.refresh.start');
      refreshTokenPromise = refreshTokenAndUpdateAuthData()
        .then((session) => session?.accessToken ?? null)
        .finally(() => {
          authDebugLog('axios.refresh.end');
          refreshTokenPromise = null;
        });
    } else {
      authDebugLog('axios.refresh.reused');
    }

    const refreshedToken = await refreshTokenPromise;
    if (!refreshedToken) {
      authDebugLog('axios.refresh.failed');
      authRefreshFailedHandler?.();
      return Promise.reject(error);
    }

    const retryConfig = error.config as InternalAxiosRequestConfig & { __retry?: boolean };
    if (retryConfig.__retry) {
      return Promise.reject(error);
    }

    retryConfig.__retry = true;
    retryConfig.headers = buildAuthHeaders(retryConfig.headers, refreshedToken);
    authDebugLog('axios.retry', { url: retryConfig.url });
    return axiosInstance.request(retryConfig);
  },
);

export const jsonHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Content-Type-Options': 'nosniff',
});

export default axiosInstance;
